import {
  toWSDateString,
  WSElement,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Batch, BatchSummaryExpand } from "@wingspanhq/wingspan-api-sdk";
import format from "date-fns/format";
import { queriesBatches } from "@query";

import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";
import { useImportBatchStatus } from "../../hooks/useImportBatchStatus";
import { PAYABLE_ENTITY_LABEL } from "../../routes/RouteImportPayableBatchDetails/constants";
import { selectorImportBatchNames } from "../../selectors/selectorImportBatchNames";

const CellImportTotal: React.FC<{ batchId: string }> = ({ batchId }) => {
  const querySummary = queriesBatches.getBatchSummary.useQuery(
    { batchId, expand: [BatchSummaryExpand.BREAKDOWN] },
    { enabled: !!batchId }
  );
  const defaultCurrency = useDefaultCurrency();

  if (querySummary.isLoading) {
    return <WSElement shimmer>loading</WSElement>;
  }

  const breakdown = querySummary.data?.breakdown;

  return (
    <WSTableCell
      text={
        breakdown && "netAmount" in breakdown
          ? formatMoney(breakdown.netAmount, defaultCurrency)
          : "--"
      }
    />
  );
};

const CellImportStatus: React.FC<{ batchId: string }> = ({ batchId }) => {
  const { pill, secondaryText } = useImportBatchStatus(batchId, true);

  return <WSTableCell pill={pill} secondaryText={secondaryText} />;
};

export const getColumns = (): WSTableColumn<Batch>[] => [
  {
    config: {
      gridTemplateSizeMax: "0.4fr",
      justify: "start",
      header: { text: "Created" }
    },
    renderFunction: rowItem => {
      const createdAt = rowItem.data.events?.createdAt;

      return (
        <WSTableCell
          text={createdAt ? toWSDateString(createdAt, "monthDayYear") : "--"}
          secondaryText={
            createdAt ? format(new Date(createdAt), "hh:mm aaa") : undefined
          }
        />
      );
    }
  },
  {
    config: {
      gridTemplateSizeMax: "0.8fr",
      justify: "start",
      header: { text: "Name" }
    },
    renderFunction: rowItem => {
      const { batchName, batchFileName } = selectorImportBatchNames({
        batch: rowItem.data,
        entityLabel: PAYABLE_ENTITY_LABEL
      });

      return <WSTableCell text={batchName} secondaryText={batchFileName} />;
    }
  },
  {
    config: {
      gridTemplateSizeMax: "0.5fr",
      justify: "start",
      header: { text: "Imported by" },
      hideOnScreens: ["XS"]
    },
    // TODO(v3-gap): v3 `Batch` carries no creator, so this column has nothing
    // to render. Restore once batches expose the acting person.
    renderFunction: () => <WSTableCell text="--" />
  },
  {
    config: {
      gridTemplateSizeMax: "0.4fr",
      justify: "start",
      header: { text: "Import total" },
      hideOnScreens: ["XS"]
    },
    renderFunction: rowItem => (
      <CellImportTotal batchId={rowItem.data.id as string} />
    )
  },
  {
    config: {
      gridTemplateSizeMax: "0.4fr",
      justify: "start",
      header: { text: "Import status" }
    },
    renderFunction: rowItem => (
      <CellImportStatus batchId={rowItem.data.id as string} />
    )
  }
];
